import type { HolidayPublic } from '@repo/types/holidays';
import type { MeetingPublic } from '@repo/types/meetings';
import type { ParentChildMeetingLocation } from '@repo/types/parent';

import { EVENT_COLOR } from '@repo/types/enums/eventColor';

export interface CalendarEvent extends MeetingPublic {
  source: 'meeting' | 'holiday';
  /**
   * Set only by the merged parent calendar, which returns one row per meeting
   * however many siblings attend it. Absent on every other source.
   */
  childIds?: number[];
  /**
   * Also parent-calendar only. Other sources leave the location to the private
   * meeting payload, which a parent is not given.
   */
  location?: ParentChildMeetingLocation | null;
}

export interface HolidayCalendarEvent {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
}

export function toCalendarMeetingEvents(meetings: MeetingPublic[]): CalendarEvent[] {
  return meetings.map((meeting) => ({
    ...meeting,
    source: 'meeting',
  }));
}

export function toCalendarHolidayEvents(holidays: Array<HolidayPublic>): CalendarEvent[] {
  return holidays.map((holiday) => ({
    id: holiday.id,
    name: `Dzień wolny - ${holiday.name}`,
    desc: '',
    type: 'stationary',
    startDate: holiday.startDate,
    endDate: holiday.endDate,
    allDay: true,
    meetingURL: null,
    isCancelled: false,
    scheduleId: null,
    updatedAt: holiday.endDate,
    createdAt: holiday.startDate,
    source: 'holiday',
  }));
}

export function mergeCalendarEvents(meetings: Array<MeetingPublic>, holidays: Array<HolidayPublic>): CalendarEvent[] {
  return [...toCalendarMeetingEvents(meetings), ...toCalendarHolidayEvents(holidays)];
}

export function isHolidayCalendarEvent(event: MeetingPublic | CalendarEvent): event is CalendarEvent {
  return 'source' in event && event.source === 'holiday';
}

export function getCalendarEventUniqueId(event: MeetingPublic | CalendarEvent): string {
  if ('source' in event) {
    return `${event.source}-${event.id}`;
  }
  return `meeting-${event.id}`;
}

export function getCalendarEventColor(event: MeetingPublic | CalendarEvent): EVENT_COLOR {
  return isHolidayCalendarEvent(event) ? EVENT_COLOR.YELLOW : EVENT_COLOR.MUTED;
}
